import { useMemo, useState, useEffect } from 'react';
import {
  Box, Typography, Stack, IconButton, FormControlLabel, Switch,
  Table, TableHead, TableRow, TableCell, TableBody, TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventNoteIcon from '@mui/icons-material/EventNote';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import {
  useCreateGlobalMeeting, useUpdateGlobalMeeting, useDeleteGlobalMeeting, type GlobalMeeting,
} from '../../api/hooks/useGlobalMeetings';
import { usePermissionContext } from '../../context/PermissionContext';
import { PROJECT_PERMISSIONS } from '@pm/shared';
import { useNotifications } from '../../context/NotificationContext';
import { useAutosave } from '../../hooks/useAutosave';
import { AutosaveIndicator } from '../../components/common/AutosaveIndicator';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { PmCard, handleInlineEditKeyDown } from '../../components/pm';
import { apiErrorMessage } from '../../lib/apiErrorMessage';

interface CoreFields {
  titel: string;
  datum: string; // YYYY-MM-DD
  uhrzeit: string; // HH:MM ('' = keine)
  ort: string;
}

const hm = (v: string | null) => (v ?? '').slice(0, 5);

/**
 * Termin-Liste unter der Timeline: bestehende globale Termine inline anlegen
 * und editieren. Standardmaessig nur zukuenftige Termine; vergangene per Toggle.
 * Detailfelder (Beschreibung/Teilnehmer/Link) ueber das Stift-Symbol -> Dialog.
 */
export function GlobalMeetingsList({
  meetings, onEditDetail,
}: {
  meetings: GlobalMeeting[];
  onEditDetail: (m: GlobalMeeting) => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');
  const { notify } = useNotifications();
  const { hasPermission } = usePermissionContext();
  const canCreate = hasPermission(PROJECT_PERMISSIONS.GLOBAL_MEETING.CREATE);
  const canUpdate = hasPermission(PROJECT_PERMISSIONS.GLOBAL_MEETING.UPDATE);
  const canDelete = hasPermission(PROJECT_PERMISSIONS.GLOBAL_MEETING.DELETE);

  const createMut = useCreateGlobalMeeting();
  const updateMut = useUpdateGlobalMeeting();
  const deleteMut = useDeleteGlobalMeeting();

  const [showPast, setShowPast] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const today = dayjs().format('YYYY-MM-DD');
  const { visible, pastCount } = useMemo(() => {
    const sorted = [...meetings].sort((a, b) =>
      `${a.datum}${a.uhrzeit ?? ''}`.localeCompare(`${b.datum}${b.uhrzeit ?? ''}`));
    const past = sorted.filter((m) => m.datum < today);
    return { visible: showPast ? sorted : sorted.filter((m) => m.datum >= today), pastCount: past.length };
  }, [meetings, showPast, today]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await deleteMut.mutateAsync(deleteId); notify(tc('status.deleted', 'Gelöscht'), 'success'); }
    catch (err) { notify(apiErrorMessage(err, tc('errors.generic', 'Fehler')), 'error'); }
    finally { setDeleteId(null); }
  };

  const head = { color: 'var(--pm-fg-subtle)', fontWeight: 600 };

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EventNoteIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>{t('globalMeeting.listTitle', 'Termine in der Projektübersicht hinzufügen')}</Typography>
        </Stack>
        {pastCount > 0 && (
          <FormControlLabel
            control={<Switch size="small" checked={showPast} onChange={(e) => setShowPast(e.target.checked)} />}
            label={<Typography variant="caption">{t('globalMeeting.showPast', 'Vergangene anzeigen ({{count}})', { count: pastCount })}</Typography>}
          />
        )}
      </Stack>

      <PmCard flush>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...head, minWidth: 220 }}>{t('globalMeeting.titel', 'Titel')}</TableCell>
              <TableCell sx={{ ...head, minWidth: 150 }}>{t('globalMeeting.datum', 'Datum')}</TableCell>
              <TableCell sx={{ ...head, minWidth: 110 }}>{t('globalMeeting.uhrzeit', 'Uhrzeit')}</TableCell>
              <TableCell sx={{ ...head, minWidth: 150 }}>{t('globalMeeting.ort', 'Ort')}</TableCell>
              <TableCell sx={head} align="right">{tc('actions.actions', 'Aktionen')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {canCreate && (
              <AddMeetingRow
                defaultDatum={today}
                onCreate={async (core) => {
                  await createMut.mutateAsync({
                    titel: core.titel, datum: core.datum, uhrzeit: core.uhrzeit || null,
                    ort: core.ort || null, beschreibung: null, teilnehmer: null, link_uri: null,
                  });
                }}
              />
            )}
            {visible.map((m) => (
              <MeetingRow
                key={m.id}
                meeting={m}
                canUpdate={canUpdate}
                canDelete={canDelete}
                past={m.datum < today}
                onSave={async (core) => {
                  await updateMut.mutateAsync({
                    id: m.id,
                    data: {
                      titel: core.titel, datum: core.datum, uhrzeit: core.uhrzeit || null, ort: core.ort || null,
                      beschreibung: m.beschreibung, teilnehmer: m.teilnehmer, link_uri: m.link_uri,
                    },
                  });
                }}
                onEditDetail={() => onEditDetail(m)}
                onDelete={() => setDeleteId(m.id)}
              />
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="caption" sx={{ color: 'var(--pm-fg-subtle)' }}>
                    {showPast
                      ? t('globalMeeting.empty', 'Noch keine Termine erfasst.')
                      : t('globalMeeting.emptyFuture', 'Keine zukünftigen Termine.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </PmCard>

      <ConfirmDialog
        open={!!deleteId}
        title={t('globalMeeting.deleteTitle', 'Termin löschen?')}
        message={t('globalMeeting.deleteConfirm', 'Diesen Termin wirklich löschen?')}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
}

/** Inline-Anlegen eines Termins (Titel + Datum Pflicht, Enter legt an). */
function AddMeetingRow({
  defaultDatum, onCreate,
}: {
  defaultDatum: string;
  onCreate: (core: CoreFields) => Promise<void>;
}) {
  const { t } = useTranslation('projects');
  const empty = (): CoreFields => ({ titel: '', datum: defaultDatum, uhrzeit: '', ort: '' });
  const [form, setForm] = useState<CoreFields>(empty);
  const [busy, setBusy] = useState(false);
  const canCreate = form.titel.trim().length > 0 && !!form.datum;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    try { await onCreate({ ...form, titel: form.titel.trim(), ort: form.ort.trim() }); setForm(empty()); }
    finally { setBusy(false); }
  };
  const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); void submit(); } };

  return (
    <TableRow sx={{ bgcolor: 'var(--pm-bg-subtle)' }}>
      <TableCell>
        <TextField variant="standard" fullWidth autoFocus placeholder={t('globalMeeting.titelPlaceholder', 'Termin-Titel eingeben…')}
          value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth type="date"
          value={form.datum} onChange={(e) => setForm({ ...form, datum: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth type="time"
          value={form.uhrzeit} onChange={(e) => setForm({ ...form, uhrzeit: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth placeholder={t('globalMeeting.ort', 'Ort')}
          value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell align="right">
        <IconButton color="primary" onClick={() => void submit()} disabled={!canCreate || busy} aria-label={t('globalMeeting.create', 'Neuer Termin')}>
          <AddIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

/** Bestehender Termin — Kernfelder inline editierbar (Autosave), Detail via Stift. */
function MeetingRow({
  meeting, canUpdate, canDelete, past, onSave, onEditDetail, onDelete,
}: {
  meeting: GlobalMeeting;
  canUpdate: boolean;
  canDelete: boolean;
  past: boolean;
  onSave: (core: CoreFields) => Promise<void>;
  onEditDetail: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation('projects');
  const { t: tc } = useTranslation('common');

  const [form, setForm] = useState<CoreFields>({
    titel: meeting.titel, datum: meeting.datum, uhrzeit: hm(meeting.uhrzeit), ort: meeting.ort ?? '',
  });

  const autosave = useAutosave<CoreFields>({
    canSave: (f) => f.titel.trim().length > 0 && !!f.datum,
    onSave: async (f) => { await onSave({ ...f, titel: f.titel.trim(), ort: f.ort.trim() }); },
  });

  useEffect(() => {
    if (autosave.isDirty) return;
    setForm({ titel: meeting.titel, datum: meeting.datum, uhrzeit: hm(meeting.uhrzeit), ort: meeting.ort ?? '' });
  }, [autosave.isDirty, meeting.titel, meeting.datum, meeting.uhrzeit, meeting.ort]);

  const update = (patch: Partial<CoreFields>) => { const next = { ...form, ...patch }; setForm(next); autosave.schedule(next); };
  const onKeyDown = (e: React.KeyboardEvent) => handleInlineEditKeyDown(e, autosave.flush);

  return (
    <TableRow hover sx={past ? { opacity: 0.6 } : undefined}>
      <TableCell>
        <TextField variant="standard" fullWidth disabled={!canUpdate} value={form.titel}
          onChange={(e) => update({ titel: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth type="date" disabled={!canUpdate} value={form.datum}
          onChange={(e) => update({ datum: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth type="time" disabled={!canUpdate} value={form.uhrzeit}
          onChange={(e) => update({ uhrzeit: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell>
        <TextField variant="standard" fullWidth disabled={!canUpdate} value={form.ort} placeholder={t('globalMeeting.ort', 'Ort')}
          onChange={(e) => update({ ort: e.target.value })} onKeyDown={onKeyDown} />
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
          {canUpdate && <AutosaveIndicator status={autosave.status} onRetry={autosave.flush} compact />}
          {canUpdate && (
            <IconButton size="small" onClick={onEditDetail} aria-label={tc('actions.edit', 'Bearbeiten')}><EditIcon fontSize="small" /></IconButton>
          )}
          {canDelete && (
            <IconButton size="small" color="error" onClick={onDelete} aria-label={tc('actions.delete', 'Löschen')}><DeleteIcon fontSize="small" /></IconButton>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}
